import type { AppState } from '../state/store';
import type { Cosmetic, CosmeticClaimCampaignStatus } from '../api/types';
import { versionedAssetUrl } from '../assets/version';
import { isPlayerSkinAssetId, playerSkinSpriteUrl } from '../cosmetics/visuals';
import { btn, esc, pill, row } from './components';
import { iconRow } from './icons';

const FOUNDER_SKIN_APPEARANCE_ASSET_ID = 'player_skin_waystation_pilgrim_01';
const FOUNDER_SKIN_ICON = '/assets/sprites/cosmetics/cosmetic_skin_waystation_pilgrim_01.png';
const FOUNDER_CLAIM_CAMPAIGN_ID = 'founder_waystation_pilgrim_01';

export function kindLabel(kind: string): string {
  // Every launch cosmetic is a full-player reskin, so a "Full reskin" badge
  // carries no information — skins get no kind badge at all.
  if (kind === 'skin') return '';
  if (kind === 'lantern_flame') return 'Lantern flame';
  if (kind === 'hub_emote') return 'Hub emote';
  if (kind === 'hood') return 'Hood';
  if (kind === 'weapon_style') return 'Weapon style';
  return kind;
}

export function kindPill(kind: string): string {
  const label = kindLabel(kind);
  return label ? `<span class="pill pill--muted">${esc(label)}</span>` : '';
}

function cosmeticIcon(cosmetic: Pick<Cosmetic, 'metadata'>): string {
  const meta = cosmetic.metadata;
  if (!meta || typeof meta !== 'object') return '';
  const icon = (meta as { icon?: unknown }).icon;
  if (typeof icon !== 'string' || icon.length === 0) return '';
  return `<img class="asset-icon asset-icon--cosmetic" src="${esc(versionedAssetUrl(icon))}" alt="" aria-hidden="true" loading="lazy" decoding="async" />`;
}

function appearanceAssetId(cosmetic: Pick<Cosmetic, 'metadata'>): string | null {
  const meta = cosmetic.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const raw = (meta as { appearanceAssetId?: unknown }).appearanceAssetId;
  return isPlayerSkinAssetId(raw) ? raw : null;
}

export function skinPreview(cosmetic: Pick<Cosmetic, 'kind' | 'slot' | 'title' | 'metadata'>): string {
  const isSkin = cosmetic.kind === 'skin' || cosmetic.slot === 'skin';
  const assetId = appearanceAssetId(cosmetic);
  const spriteUrl = playerSkinSpriteUrl(assetId);
  if (isSkin && assetId && spriteUrl) {
    return `
      <figure class="skin-preview skin-preview--animated" data-skin-preview="animated" data-appearance-asset-id="${esc(assetId)}" aria-label="${esc(cosmetic.title)} idle skin preview">
        <span class="skin-preview__stage" aria-hidden="true">
          <span class="skin-preview__sprite" style="--skin-sheet: url('${esc(versionedAssetUrl(spriteUrl))}');"></span>
        </span>
      </figure>`;
  }

  if (!isSkin) return cosmeticIcon(cosmetic);

  const icon = cosmeticIcon(cosmetic);
  return `
    <figure class="skin-preview skin-preview--fallback" data-skin-preview="fallback" aria-label="${esc(cosmetic.title)} skin preview unavailable">
      <span class="skin-preview__stage" aria-hidden="true">
        ${icon || `<span class="skin-preview__fallback-label">${esc(cosmetic.title.slice(0, 2).toUpperCase())}</span>`}
      </span>
    </figure>`;
}

function stockLine(cosmetic: Cosmetic): string {
  if (cosmetic.stockTotal === null || cosmetic.stockTotal === undefined) return '';
  if (cosmetic.soldOut || (cosmetic.stockRemaining ?? 0) <= 0) {
    return `<div class="item__meta cosmetic-stock cosmetic-stock--sold-out">Sold out · ${esc(cosmetic.stockTotal)} total</div>`;
  }
  return `<div class="item__meta cosmetic-stock">${esc(cosmetic.stockRemaining)} / ${esc(cosmetic.stockTotal)} left · ${esc(cosmetic.stockTotal)} total</div>`;
}

function isPrimaryShopCosmetic(cosmetic: Cosmetic): boolean {
  return (cosmetic.acquisitionKind || 'shop') === 'shop' && cosmetic.price > 0;
}

function collectionById(state: AppState): Map<string, Cosmetic> {
  return new Map((state.myCosmetics ?? []).map((c) => [c.id, c]));
}

function headerCard(state: AppState): string {
  return `
    <section class="card card--wide cosmetics-head">
      <div class="run__head">
        <h2><span class="ember-dot" aria-hidden="true"></span> Cosmetics</h2>
        <div class="btn-row">
          ${btn('Refresh', 'cosmetics-refresh', {}, { variant: 'ghost' })}
          ${btn('Back to Waystation', 'goto-waystation', {}, { variant: 'ghost' })}
        </div>
      </div>
      <p class="muted">Visual-only Gold sinks. Cosmetics do not change combat, loot, costs, Ferry Passes, cash-out value, or spinner odds.</p>
      ${iconRow('Your Gold', 'gold', `<strong>${esc(state.cosmeticsGoldBalance ?? 0)}</strong>`)}
    </section>`;
}

/**
 * Owned-copy accounting with graceful fallbacks for payloads that predate
 * multi-copy ownership (an owned cosmetic is always at least one copy).
 */
export function copyCounts(cosmetic: Cosmetic): { quantity: number; listedCount: number; listableCount: number } {
  const listedCount = Math.max(0, cosmetic.listedCount ?? (cosmetic.listed ? 1 : 0));
  const rawQuantity = cosmetic.quantity ?? 0;
  const quantity = cosmetic.owned ? Math.max(1, rawQuantity, listedCount) : Math.max(0, rawQuantity);
  // Always derived (quantity − listed − equipped copy) so stale payloads can
  // never disagree with the counts shown beside it.
  const listableCount = Math.max(0, quantity - listedCount - (cosmetic.equipped ? 1 : 0));
  return { quantity, listedCount, listableCount };
}

/**
 * Owned-cosmetic actions for the Inventory Collection: equip/unequip and
 * informational badges ONLY. Listing (and cancelling) cosmetics for sale lives
 * exclusively on the Marketplace Cosmetics category — the collection never
 * renders price inputs or list/cancel controls.
 */
function ownedAction(cosmetic: Cosmetic): string {
  const { quantity, listedCount } = copyCounts(cosmetic);
  const ownedPill = pill(`Owned ×${quantity}`, 'muted');
  // Equip is by cosmetic identity and needs one unlisted copy.
  const equippable = quantity - listedCount >= 1;
  const equip = cosmetic.equipped
    ? `${pill('equipped', 'ok')} ${btn('Unequip', 'cosmetic-unequip', { cosmetic: cosmetic.id }, { variant: 'ghost' })}`
    : equippable
      ? `${ownedPill} ${btn('Equip', 'cosmetic-equip', { cosmetic: cosmetic.id }, { variant: 'primary' })}`
      : `${ownedPill} ${pill('all copies listed — cannot equip', 'warn')}`;
  const listedBadge = listedCount > 0 ? ` ${pill(`${listedCount} listed on Marketplace`, 'muted')}` : '';
  return `${equip}${listedBadge}`;
}

function cosmeticCard(
  cosmetic: Cosmetic,
  owned: Cosmetic | undefined,
  goldBalance: number,
  context: 'shop' | 'collection' = 'shop',
): string {
  const isOwned = !!owned;
  const affordable = goldBalance >= cosmetic.price;
  let action: string;
  if (isOwned && context === 'shop') {
    // Multi-copy ownership: repeat shop purchases are allowed while stock remains.
    const buyAnother = cosmetic.soldOut
      ? `${pill('Sold out', 'warn')}`
      : affordable
        ? btn(`Buy another · ${cosmetic.price} Gold`, 'cosmetic-buy', { cosmetic: cosmetic.id }, { variant: 'primary' })
        : `${pill('not enough Gold', 'warn')} ${btn(`Buy another · ${cosmetic.price} Gold`, 'cosmetic-buy', { cosmetic: cosmetic.id }, { disabled: true })}`;
    action = `${pill(`Owned ×${copyCounts(owned).quantity}`, 'ok')} ${buyAnother}`;
  } else if (!isOwned && cosmetic.soldOut) {
    action = `${pill('Sold out', 'warn')} ${btn('Sold out', 'cosmetic-buy', { cosmetic: cosmetic.id }, { disabled: true })}`;
  } else if (!isOwned && !affordable) {
    action = `${pill('not enough Gold', 'warn')} ${btn(`${cosmetic.price} Gold`, 'cosmetic-buy', { cosmetic: cosmetic.id }, { disabled: true })}`;
  } else if (!isOwned) {
    action = btn(`Buy · ${cosmetic.price} Gold`, 'cosmetic-buy', { cosmetic: cosmetic.id }, { variant: 'primary' });
  } else {
    action = ownedAction(owned);
  }
  return `
    <div class="item cosmetic-item ${context === 'collection' ? 'collection-card' : 'shop-card'}">
      ${skinPreview(cosmetic)}
      <div class="item__head">
        ${cosmeticIcon(cosmetic)}
        <span class="rarity rarity--common">${esc(cosmetic.title)}</span>
        ${kindPill(cosmetic.kind)}
      </div>
      <div class="item__meta">${esc(cosmetic.description)}</div>
      <div class="item__meta">${esc(cosmetic.slot)} · ${esc(cosmetic.price)} Gold</div>
      ${stockLine(cosmetic)}
      <div class="btn-row">${action}</div>
    </div>`;
}

function shopCard(state: AppState): string {
  const shop = (state.cosmeticsShop ?? []).filter(isPrimaryShopCosmetic);
  const owned = collectionById(state);
  const gold = state.cosmeticsGoldBalance ?? 0;
  const body = shop.length > 0
    ? `<div class="items">${shop.map((c) => cosmeticCard(c, owned.get(c.id), gold)).join('')}</div>`
    : '<p class="muted">No cosmetics are in the shop right now.</p>';
  return `
    <section class="card card--wide">
      <h2>Shop</h2>
      ${body}
    </section>`;
}

function claimState(status: CosmeticClaimCampaignStatus): { label: string; tone: 'ok' | 'warn' | 'muted' } {
  if (status.expired) return { label: 'Claim expired', tone: 'warn' };
  if (status.soldOut) return { label: 'Claim sold out', tone: 'warn' };
  if (status.alreadyClaimed || status.walletAlreadyClaimed) return { label: 'Already claimed', tone: 'ok' };
  if (status.lastCheckResult === 'unverified') return { label: 'RPC unavailable', tone: 'warn' };
  if (status.canClaim) return { label: 'Eligible', tone: 'ok' };
  return { label: 'Not eligible', tone: 'warn' };
}

function claimPreview(status: CosmeticClaimCampaignStatus): string {
  if (status.campaignId !== FOUNDER_CLAIM_CAMPAIGN_ID) return skinPreview(status);
  const metadata = status.metadata && typeof status.metadata === 'object'
    ? { icon: FOUNDER_SKIN_ICON, ...(status.metadata as Record<string, unknown>) }
    : {
        appearanceAssetId: FOUNDER_SKIN_APPEARANCE_ASSET_ID,
        icon: FOUNDER_SKIN_ICON,
        visualOnly: true,
      };
  return skinPreview({ ...status, metadata });
}

function requirementLine(status: CosmeticClaimCampaignStatus): string {
  const requirements = status.requirements.length > 0
    ? status.requirements
    : status.threshold
      ? [{ symbol: 'OBOL', threshold: status.threshold }]
      : [];
  if (requirements.length === 0) return 'No token requirement';
  return `Hold ${requirements.map((r) => `${r.threshold} $${r.symbol}`).join(' + ')}`;
}

function supplyLine(status: CosmeticClaimCampaignStatus): string {
  if (status.capTotal === null) return `Unlimited global supply · ${status.claimedCount} claimed`;
  if (status.soldOut) return `${status.claimedCount} / ${status.capTotal} claimed · 0 remaining`;
  return `${status.remaining} remaining · ${status.claimedCount} / ${status.capTotal} claimed`;
}

function formatClaimDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

function availabilityLine(status: CosmeticClaimCampaignStatus): string {
  if (!status.claimEndAt) return status.capTotal === null ? 'Available while campaign is active' : 'Available until supply is claimed';
  return `${status.expired ? 'Ended' : 'Available until'} ${formatClaimDate(status.claimEndAt)}`;
}

function missingClaimCard(): string {
    return `
      <section class="card card--wide founder-claim">
        <div class="founder-claim__body">
          ${claimPreview({
            campaignId: FOUNDER_CLAIM_CAMPAIGN_ID,
            cosmeticId: 'cosmetic_skin_waystation_pilgrim_01',
            title: 'Waystation Pilgrim',
            description: 'Founder claim skin.',
            kind: 'skin',
            slot: 'skin',
            metadata: {
              appearanceAssetId: FOUNDER_SKIN_APPEARANCE_ASSET_ID,
              icon: FOUNDER_SKIN_ICON,
              visualOnly: true,
            },
            capTotal: 500,
            claimedCount: 0,
            remaining: 500,
            threshold: 1000,
            requirements: [{ symbol: 'OBOL', threshold: 1000, state: 'unverified' }],
            eligibilityState: null,
            lastCheckResult: 'unverified',
            alreadyClaimed: false,
            walletAlreadyClaimed: false,
            soldOut: false,
            expired: false,
            active: false,
            claimStartAt: null,
            claimEndAt: null,
            canClaim: false,
          })}
          <div class="founder-claim__details">
            <div class="run__head">
              <h2>Waystation Pilgrim</h2>
              ${pill('status unavailable', 'warn')}
            </div>
            <p class="muted">Waystation Pilgrim — first 500 eligible holders.</p>
            ${btn('Refresh', 'cosmetics-refresh', {}, { variant: 'ghost' })}
          </div>
        </div>
      </section>`;
}

function claimCampaignCard(status: CosmeticClaimCampaignStatus): string {
  const statePill = claimState(status);
  const founder = status.campaignId === FOUNDER_CLAIM_CAMPAIGN_ID;
  return `
    <section class="card card--wide founder-claim cosmetic-claim" data-claim-campaign="${esc(status.campaignId)}">
      <div class="founder-claim__body">
        ${claimPreview(status)}
        <div class="founder-claim__details">
          <div class="run__head">
            <h2>${esc(status.title)}</h2>
            ${pill(statePill.label, statePill.tone)}
          </div>
          <p class="muted">${esc(status.description || (founder ? `${status.title} — first 500 eligible holders.` : `${status.title} claim.`))}</p>
          <div class="kv"><dt>Requirement</dt><dd>${esc(requirementLine(status))}</dd></div>
          <div class="kv"><dt>Supply</dt><dd>${esc(supplyLine(status))}</dd></div>
          <div class="kv"><dt>Availability</dt><dd>${esc(availabilityLine(status))}</dd></div>
          <div class="btn-row">
            ${btn(status.alreadyClaimed || status.walletAlreadyClaimed ? 'Claimed' : `Claim ${status.title}`, 'cosmetic-claim', { campaign: status.campaignId }, { variant: 'primary', disabled: !status.canClaim })}
          </div>
        </div>
      </div>
    </section>`;
}

function claimCards(state: AppState): string {
  const campaigns = state.cosmeticClaimCampaigns && state.cosmeticClaimCampaigns.length > 0
    ? state.cosmeticClaimCampaigns
    : state.cosmeticFounderClaim
      ? [state.cosmeticFounderClaim]
      : [];
  return campaigns.length > 0 ? campaigns.map(claimCampaignCard).join('') : missingClaimCard();
}

/**
 * Owned cosmetics "Collection" card, shared with the Inventory view (the
 * Cosmetics page keeps claims/shop; the owned collection lives under the
 * Inventory Stash). Animated preview cards + equip/unequip only — listing
 * cosmetics for sale happens exclusively on the Marketplace.
 */
export function ownedCosmeticsCollectionCard(state: AppState): string {
  const mine = state.myCosmetics ?? [];
  const body = mine.length > 0
    ? `<div class="items cosmetic-collection-grid">${mine.map((c) => cosmeticCard(c, c, state.cosmeticsGoldBalance ?? 0, 'collection')).join('')}</div>`
    : '<p class="muted">No cosmetics owned yet.</p>';
  const equipped = state.equippedCosmetics ?? [];
  const equippedLine = equipped.length > 0
    ? equipped.map((c) => `<span class="pill pill--ok">${esc(c.title)}</span>`).join(' ')
    : '<span class="muted">none equipped</span>';
  return `
    <section class="card card--wide cosmetics-collection-card">
      <h2>Collection</h2>
      ${row('Equipped', equippedLine)}
      ${body}
    </section>`;
}

/**
 * The Cosmetics page keeps claim campaigns + the paid shop. Player-to-player
 * trading lives under the Marketplace Cosmetics category, and the owned
 * collection lives in the Inventory — neither is duplicated here.
 */
export function renderCosmetics(state: AppState): string {
  if (!state.session) {
    return `<section class="card"><h2>Cosmetics</h2><p class="muted">Connect a wallet to buy visual cosmetics with Gold.</p></section>`;
  }
  return `
    <div class="grid">
      ${headerCard(state)}
      ${claimCards(state)}
      ${shopCard(state)}
      <section class="card card--wide">
        <h2>Trading &amp; collection</h2>
        <p class="muted">Player-to-player cosmetic trading lives under the Marketplace’s Cosmetics category. Your owned collection lives in the Inventory, under the Stash.</p>
        <div class="btn-row">${btn('Open Marketplace', 'goto-marketplace', {}, { variant: 'ghost' })}${btn('Open Inventory', 'goto-inventory', {}, { variant: 'ghost' })}</div>
      </section>
    </div>`;
}
